import React from "react"
import {
  Col,
  FormGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Formik } from "formik"
import * as Yup from "yup"
import CustomLabel from "components/Common/CustomLabel"
import { showAlert } from "store/reducers/alertReducer"
import { generateIdCards } from "../../../store/actions/cardsAction"
import CustomField from "../../../components/Common/CustomField"
import { formatDateForAPI } from "../../../components/Common/Utils"

const GenerateIDCardsModal = ({ isOpen, toggle, trigger }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const validationSchema = Yup.object().shape({
    collection_date: Yup.date()
      .typeError(t("Invalid date"))
      .min(today, t("Date cannot be in the past"))
      .required(t("Field is required")),
    count: Yup.number()
      .min(0, t("Count cannot be less than 0"))
      .max(2000, t("Count cannot be more than 2000"))
      .required(t("Field is required")),
  })

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      ...values,
      collection_date: formatDateForAPI(values.collection_date)
    }
    dispatch(generateIdCards(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload || {}
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          toggle()
          trigger()
        } else {
          dispatch(showAlert({ text: message || "An error occurred", type: "error" }))
        }
      })
      .catch((err) => {
        console.error("Submission error:", err)
        dispatch(showAlert({ text: "Unexpected error", type: "error" }))
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>{t("Generate ID Cards")}</ModalHeader>
      <Formik
        initialValues={{
          collection_date: today,
          count: 0,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, handleBlur, handleSubmit, isValid, isSubmitting }) => (
          <>
            <ModalBody>
              <Row>
                <Col xs="12" sm="6" className="mb-1">
                  <FormGroup>
                    <div className="form_field">
                      <CustomLabel label={t("Select Collection Date")} isRequired />
                      <Flatpickr
                        options={{ dateFormat: "d-m-Y" }}
                        value={values.collection_date}
                        onChange={(selectedDates) =>
                          setFieldValue("collection_date", selectedDates[0] || null)
                        }
                        className="form-control"
                        onBlur={handleBlur}
                      />
                      {errors.collection_date && touched.collection_date && (
                        <div className="text-danger fw-semibold mt-1">
                          {errors.collection_date}
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </Col>

                <Col xs="12" sm="6" className="mb-1">
                  <div className="form_field">
                    <CustomLabel htmlFor="count" label={t("Count")} isRequired />
                    <CustomField name="count" type="number" />
                  </div>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="main_btn disabled-button fit_content px-4"
                  onClick={toggle}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="main_btn fit_content px-4"
                  onClick={handleSubmit}
                  disabled={!isValid || isSubmitting}
                >
                  {t("Submit")}
                </button>
              </div>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default GenerateIDCardsModal
